import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  addDealInternalNote,
  listDealInternalNotes,
  type Deal,
  type DealInternalNoteContext,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { NoteBody, NoteEntry, NoteThread } from "@/components/deals/NoteThread";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";

const NOTE_MAX = 2000;

/** Human label for the adjudication stage a note belongs to (CHP-401 — "the
 * context of the note, i.e. deal registration / deal extension"). */
const CONTEXT_LABEL: Record<DealInternalNoteContext, string> = {
  general: "General",
  registration: "Deal registration",
  extension: "Deal extension",
  closed_won: "Closed-won",
};

const CONTEXT_OPTIONS: DealInternalNoteContext[] = [
  "general",
  "registration",
  "extension",
  "closed_won",
];

/**
 * Internal notes on the detailed deal view (CHP-401), CM/RA/RM only.
 *
 * The notes live on a SEPARATE table from "Deal Owner Notes" so the visibility
 * boundary is structural: the backend endpoints are manager-gated and a BDM/PO
 * never reaches them. This section is therefore rendered only inside the
 * CM/RA/RM detail (ManagerDealDetail) — never on the partner-facing view.
 *
 * Each note shows the body, its context (deal registration / extension / …),
 * the author, and a timestamp. The closed-won rejection reason is persisted by
 * the backend as a `closed_won` note (CHP-413) and surfaces here.
 */
export function DealInternalNotesSection({ deal }: { deal: Deal }) {
  const { accessToken, data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Defence in depth: the backend gates this, but never render the section for
  // a non-manager even if the component is mounted in the wrong tree.
  const isManager = user?.role === "channel_manager" || user?.role === "admin";

  const notesQuery = useQuery({
    queryKey: ["deals", "internal-notes", deal.id],
    queryFn: () => listDealInternalNotes(accessToken!, deal.id),
    enabled: !!accessToken && isManager,
  });

  const [draft, setDraft] = React.useState("");
  const [context, setContext] = React.useState<DealInternalNoteContext>("general");
  const [error, setError] = React.useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => addDealInternalNote(accessToken!, deal.id, { body: draft.trim(), context }),
    onSuccess: () => {
      setDraft("");
      setContext("general");
      setError(null);
      void queryClient.invalidateQueries({
        queryKey: ["deals", "internal-notes", deal.id],
      });
      toast({ variant: "success", description: "Internal note saved" });
    },
    onError: () => setError("Couldn't save the note. Refresh and try again."),
  });

  if (!isManager) return null;

  const notes = notesQuery.data ?? [];

  return (
    <section>
      <h2 className="mb-1 text-base font-semibold">Internal notes</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Visible to ResetData staff only. Never shared with the channel partner.
      </p>

      <NoteThread
        entries={notes}
        isLoading={notesQuery.isLoading}
        isError={notesQuery.isError}
        loadingLabel="Loading notes…"
        emptyLabel="No internal notes yet."
        errorLabel="Couldn't load internal notes. Please try again."
        onRetry={() => void notesQuery.refetch()}
        renderEntry={(note) => (
          <NoteEntry
            key={note.id}
            author={note.author_name}
            timestamp={note.created_at}
            headerAction={
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {CONTEXT_LABEL[note.context]}
              </span>
            }
          >
            <NoteBody>{note.body}</NoteBody>
          </NoteEntry>
        )}
      />

      <div className="mt-4 space-y-2">
        <label className="block text-sm font-medium" htmlFor="deal-internal-note-input">
          Add an internal note
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-muted-foreground" htmlFor="deal-internal-note-context">
            Context
          </label>
          <select
            id="deal-internal-note-context"
            value={context}
            onChange={(e) => setContext(e.target.value as DealInternalNoteContext)}
            className="rounded border border-border bg-background p-2 text-sm"
            disabled={add.isPending}
          >
            {CONTEXT_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {CONTEXT_LABEL[value]}
              </option>
            ))}
          </select>
        </div>
        <Textarea
          id="deal-internal-note-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={NOTE_MAX}
          rows={3}
          placeholder="Visible to ResetData staff only. Never shared with the channel partner."
          className="w-full"
          disabled={add.isPending}
        />
        <div>
          <Button
            type="button"
            onClick={() => {
              if (!draft.trim()) return;
              add.mutate();
            }}
            disabled={!draft.trim()}
            loading={add.isPending}
            loadingLabel="Saving…"
          >
            Add internal note
          </Button>
        </div>
      </div>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
